import {attachAiosMessageLayout} from "./aios_message_layout.js"

const STORAGE_KEY = "seiva_monica_open"
const WIDTH_STORAGE_KEY = "seiva_monica_drawer_width"
const HTML_CLASS = "monica-drawer-open"
const RESIZING_CLASS = "monica-drawer-resizing"
const COMMITTEE_HITL_PRESENT_CLASS = "committee-hitl-present"
const COMMITTEE_HITL_EXPANDED_CLASS = "committee-hitl-expanded"
const COMMITTEE_LAYOUT_CHANGED_EVENT = "seiva:committee-layout-changed"
const DEFAULT_WIDTH = 576
const MIN_WIDTH = DEFAULT_WIDTH * 0.75
const MAX_WIDTH = DEFAULT_WIDTH * 1.5
const MIN_VISIBLE_APP_WIDTH = 320
const COMMITTEE_HITL_COLLAPSED_WIDTH = 64
const COMMITTEE_HITL_EXPANDED_WIDTH = 400

// Reads `data-open` from the drawer root and toggles a class on <html>.
// CSS uses that class to push the main layout left by the drawer's width
// on lg+, so the platform stays usable while the chat is visible on the right.
function syncRootClass(el) {
  const open = el.dataset.open === "true"
  document.documentElement.classList.toggle(HTML_CLASS, open)
}

function committeeHitlWidth() {
  const root = document.documentElement
  if (!root.classList.contains(COMMITTEE_HITL_PRESENT_CLASS)) return 0

  return root.classList.contains(COMMITTEE_HITL_EXPANDED_CLASS)
    ? COMMITTEE_HITL_EXPANDED_WIDTH
    : COMMITTEE_HITL_COLLAPSED_WIDTH
}

function maxAllowedWidth() {
  if (window.innerWidth < 1024) return DEFAULT_WIDTH

  return Math.max(
    MIN_WIDTH,
    Math.min(
      MAX_WIDTH,
      window.innerWidth - MIN_VISIBLE_APP_WIDTH - committeeHitlWidth()
    )
  )
}

function clampWidth(width) {
  return Math.round(Math.min(Math.max(width, MIN_WIDTH), maxAllowedWidth()))
}

function readStoredWidth() {
  const parsed = Number.parseInt(localStorage.getItem(WIDTH_STORAGE_KEY), 10)
  return clampWidth(Number.isFinite(parsed) ? parsed : DEFAULT_WIDTH)
}

function applyDrawerWidth(width) {
  const next = clampWidth(width)
  document.documentElement.style.setProperty("--monica-drawer-width", `${next}px`)
  if (document.documentElement.classList.contains("aios-document")) {
    document.documentElement.style.setProperty("--chat-w", `${next}px`)
    const handle = document.querySelector("[data-aios-drawer] [data-monica-resize-handle]")
    handle?.setAttribute("aria-valuenow", String(next))
    handle?.setAttribute("aria-valuemax", String(maxAllowedWidth()))
  }
  return next
}

const MatthewDrawer = {
  mounted() {
    syncRootClass(this.el)
    if (this.el.dataset.aiosDrawer) this.aiosMessages = attachAiosMessageLayout(this.el)
    this.syncAiosFocus()
    this.drawerWidth = applyDrawerWidth(readStoredWidth())

    this.onPointerMove = event => {
      if (!this.resizing) return
      const delta = this.startX - event.clientX
      this.drawerWidth = applyDrawerWidth(this.startWidth + delta)
    }

    this.stopResize = () => {
      if (!this.resizing) return
      this.resizing = false
      this.resizeHandle?.classList.remove("is-dragging")
      document.body.classList.remove(RESIZING_CLASS)
      window.removeEventListener("pointermove", this.onPointerMove)
      window.removeEventListener("pointerup", this.stopResize)
      window.removeEventListener("pointercancel", this.stopResize)
      localStorage.setItem(WIDTH_STORAGE_KEY, String(this.drawerWidth))
    }

    this.onPointerDown = event => {
      if (window.innerWidth < 1024) return
      event.preventDefault()
      this.resizeHandle?.focus({preventScroll: true})
      this.resizing = true
      this.resizeHandle?.classList.add("is-dragging")
      this.startX = event.clientX
      this.startWidth = this.drawerWidth || readStoredWidth()
      document.body.classList.add(RESIZING_CLASS)
      window.addEventListener("pointermove", this.onPointerMove)
      window.addEventListener("pointerup", this.stopResize)
      window.addEventListener("pointercancel", this.stopResize)
    }

    this.onHandleKeydown = event => {
      if (window.innerWidth < 1024) return
      const step = event.shiftKey ? 48 : 24
      const keyMap = {
        ArrowLeft: step,
        ArrowRight: -step,
        Home: MIN_WIDTH - this.drawerWidth,
        End: MAX_WIDTH - this.drawerWidth
      }
      const delta = keyMap[event.key]
      if (delta === undefined) return
      event.preventDefault()
      this.drawerWidth = applyDrawerWidth((this.drawerWidth || readStoredWidth()) + delta)
      localStorage.setItem(WIDTH_STORAGE_KEY, String(this.drawerWidth))
    }

    this.onWindowResize = () => {
      this.drawerWidth = applyDrawerWidth(this.drawerWidth || readStoredWidth())
      localStorage.setItem(WIDTH_STORAGE_KEY, String(this.drawerWidth))
    }

    this.onCommitteeLayoutChanged = () => {
      this.drawerWidth = applyDrawerWidth(this.drawerWidth || readStoredWidth())
    }

    this.onDrawerKeydown = event => {
      if (!this.el.dataset.aiosDrawer || this.el.dataset.open !== "true" || event.key !== "Escape" || event.defaultPrevented) return
      if (event.target.closest('[role="menu"],[data-mention-menu]')) return
      event.preventDefault()
      this.pushEventTo(this.el, "toggle_drawer", {})
    }
    this.onAiosTab = event => {
      if (!this.el.dataset.aiosDrawer || this.el.dataset.open !== "true" || window.innerWidth >= 1024 || event.key !== "Tab") return
      const elements = Array.from(this.el.querySelectorAll('button:not(:disabled),a[href],textarea:not(:disabled),input:not(:disabled),select:not(:disabled),[tabindex="0"]')).filter(el => el.getClientRects().length > 0)
      const first = elements[0], last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    this.el.addEventListener("keydown", this.onAiosTab)
    this.el.addEventListener("keydown", this.onDrawerKeydown)
    this.bindResizeHandle()
    window.addEventListener("resize", this.onWindowResize)
    window.addEventListener(COMMITTEE_LAYOUT_CHANGED_EVENT, this.onCommitteeLayoutChanged)

    // Sync localStorage whenever the server changes the drawer state
    this.handleEvent("monica:state_changed", ({ open }) => {
      localStorage.setItem(STORAGE_KEY, open ? "true" : "false")
    })
  },
  updated() {
    syncRootClass(this.el)
    this.aiosMessages?.sync()
    this.syncAiosFocus()
    this.bindResizeHandle()
    this.drawerWidth = applyDrawerWidth(this.drawerWidth || readStoredWidth())
  },
  syncAiosFocus() {
    if (!this.el.dataset.aiosDrawer) return
    const open = this.el.dataset.open === "true"
    if (open === this.aiosWasOpen) return
    if (open) {
      this.aiosReturnFocus = document.activeElement
      const input = this.el.querySelector('[data-testid="matthew-input"]:not(:disabled)') || this.el.querySelector('[data-testid="matthew-close"]')
      input?.focus?.({preventScroll: true})
    } else this.aiosReturnFocus?.focus?.({preventScroll: true})
    this.aiosWasOpen = open
  },
  bindResizeHandle() {
    const handle = this.el.querySelector("[data-monica-resize-handle]")
    if (handle === this.resizeHandle) return

    if (this.resizeHandle) {
      this.resizeHandle.removeEventListener("pointerdown", this.onPointerDown)
      this.resizeHandle.removeEventListener("keydown", this.onHandleKeydown)
    }

    this.resizeHandle = handle

    if (this.resizeHandle) {
      this.resizeHandle.addEventListener("pointerdown", this.onPointerDown)
      this.resizeHandle.addEventListener("keydown", this.onHandleKeydown)
    }
  },
  destroyed() {
    this.aiosMessages?.destroy()
    this.el.removeEventListener("keydown", this.onDrawerKeydown)
    this.el.removeEventListener("keydown", this.onAiosTab)
    if (this.resizing) this.stopResize()
    document.documentElement.classList.remove(HTML_CLASS)
    document.body.classList.remove(RESIZING_CLASS)
    window.removeEventListener("resize", this.onWindowResize)
    window.removeEventListener(COMMITTEE_LAYOUT_CHANGED_EVENT, this.onCommitteeLayoutChanged)
    window.removeEventListener("pointermove", this.onPointerMove)
    window.removeEventListener("pointerup", this.stopResize)
    window.removeEventListener("pointercancel", this.stopResize)

    if (this.resizeHandle) {
      this.resizeHandle.removeEventListener("pointerdown", this.onPointerDown)
      this.resizeHandle.removeEventListener("keydown", this.onHandleKeydown)
    }
  }
}

export default MatthewDrawer
